import {
    DataScopesEnum,
    OrganizationTypesEnum,
    RolesEnum,
    StrategyTypes,
    UserRightsEnum,
} from '../utilities/Constants';
import { IOrganizationRoleModel, IUserModel } from '../interfaces/IUser';

import Auth from '../stores/authentication';
import { ILookupModel } from '../interfaces/ILookup';
import { LocalRoutes } from '../utilities/LocalRoutes';

export default class Authorization {
    static getUserActiveZoneRights(user?: IUserModel, currentZoneGuid?: string): ILookupModel[] {
        let defaultOrgMatch: IOrganizationRoleModel = {};
        let userRights: ILookupModel[] = [];

        if (
            user &&
            currentZoneGuid &&
            user.organizationRoles &&
            user.organizationRoles.filter((x) => x.guid == currentZoneGuid).length > 0
        ) {
            defaultOrgMatch = user.organizationRoles.filter((x) => x.guid == currentZoneGuid)[0];
            userRights = defaultOrgMatch.rights || [];
        }
        return userRights;
    }

    static isAuthorizedToAtLeastOneRoute = (
        routes: string[],
        user?: IUserModel,
        currentZoneGuid?: string
    ) => routes.some((route) => Authorization.isAuthorizedToRoute(route, user, currentZoneGuid));

    static isAuthorizedToRoute(route: string, user?: IUserModel, currentZoneGuid?: string) {
        const isSystemAdmin = user ? Auth.isSystemAdmin(user) : false;
        const userRights = this.getUserActiveZoneRights(user, currentZoneGuid);
        const isOrganizationAdmin = user ? Auth.isOrganizationAdmin(user) : false;
        const isMasterAdmin = user ? Auth.isMasterAdmin(user) : false;
        const isCompanyUser =
            user?.homeOrganization?.organizationType?.id === OrganizationTypesEnum.Company.Value;
        const hasAtleastOneNationalDataScope =
            user && Authorization.userHasAtleastOneNationalDataScope(user);

        switch (route) {
            case LocalRoutes.CreateInvitation:
                return isSystemAdmin || isOrganizationAdmin || isMasterAdmin;
            case LocalRoutes.Zones:
            case LocalRoutes.ZoneDetail:
            case LocalRoutes.ZoneNew:
            case LocalRoutes.OrganizationDetail:
                return isSystemAdmin;
            case LocalRoutes.Users:
                return isSystemAdmin || isOrganizationAdmin || isMasterAdmin;
            case LocalRoutes.UserDetail:
                return isSystemAdmin || isOrganizationAdmin || isMasterAdmin;
            case LocalRoutes.AssertionDescriptors:
                return isSystemAdmin;
            case LocalRoutes.AssertionDescriptorDetail:
                return isSystemAdmin;
            case LocalRoutes.Cases:
                if (userRights.filter((x) => x.id == UserRightsEnum.ViewCaseList).length > 0)
                    return true;
                break;
            case LocalRoutes.CaseFile:
                if (
                    userRights.filter(
                        (x) =>
                            x.id == UserRightsEnum.ViewCaseFileBasic ||
                            x.id == UserRightsEnum.ViewCaseFile
                    ).length > 0
                )
                    return true;
                break;
            case LocalRoutes.CaseAssessment:
                if (
                    userRights.filter(
                        (x) =>
                            x.id == UserRightsEnum.AssessmentBestFitCasesGet ||
                            x.id == UserRightsEnum.AssessmentCaseStatisticsGet ||
                            x.id == UserRightsEnum.AssessmentPredictedOutcomeGet ||
                            x.id == UserRightsEnum.AssessmentThreatScoreGet
                    ).length > 0
                )
                    return true;
                break;
            case LocalRoutes.CaseFileNew:
                if (userRights.filter((x) => x.id == UserRightsEnum.AddNewCase).length > 0)
                    return true;
                break;
            case LocalRoutes.CaseEasyUpdate:
                // Easy Update Access is based on whether you have access to edit case File at the zone level
                if (userRights.filter((x) => x.id == UserRightsEnum.EasyUpdate).length > 0)
                    return true;
                break;
            case LocalRoutes.ReportTypeList:
                if (userRights.filter((x) => x.id == UserRightsEnum.Reports).length > 0)
                    return true;
                break;
            case LocalRoutes.ReportDetail:
                if (userRights.filter((x) => x.id == UserRightsEnum.Reports).length > 0)
                    return true;
                break;
            case LocalRoutes.UserReportList:
                return true;
            case LocalRoutes.UserZoneProfile:
                return true;
            case LocalRoutes.CasePlan:
                if (
                    userRights.filter(
                        (x) =>
                            x.id == UserRightsEnum.FullPlanningAndExecution ||
                            x.id == UserRightsEnum.ReadOnlyPlanningAndExecution ||
                            x.id == UserRightsEnum.FirmLevelPlanningAndExecution
                    ).length > 0
                )
                    return true;
                break;
            case LocalRoutes.CasePlanTaskDetail:
                if (
                    userRights.filter(
                        (x) =>
                            x.id == UserRightsEnum.FullPlanningAndExecution ||
                            x.id == UserRightsEnum.ReadOnlyPlanningAndExecution ||
                            x.id == UserRightsEnum.FirmLevelPlanningAndExecution
                    ).length > 0
                )
                    return true;
                break;
            case LocalRoutes.UserTasks:
                return true;
            case LocalRoutes.WorkProductDetail:
                return false;
            case LocalRoutes.TaskBarSetting:
                return true;
            case LocalRoutes.Board:
                if (userRights.filter((x) => x.id == UserRightsEnum.Board).length > 0) return true;
                break;
            case LocalRoutes.BoardCaseDetail:
                if (userRights.filter((x) => x.id == UserRightsEnum.Board).length > 0) return true;
                break;
            case LocalRoutes.BoardViewDetail:
                if (userRights.filter((x) => x.id == UserRightsEnum.Board).length > 0) return true;
                break;
            case LocalRoutes.Query:
                if (
                    userRights.filter(
                        (x) =>
                            x.id == UserRightsEnum.ViewCaseFile ||
                            x.id == UserRightsEnum.ViewCaseFileBasic ||
                            x.id == UserRightsEnum.ViewCaseNotes ||
                            x.id == UserRightsEnum.ViewStrategicPlan ||
                            x.id == UserRightsEnum.ViewExecutionPlan ||
                            x.id == UserRightsEnum.ViewPlaintiffsFirmStrategy ||
                            x.id == UserRightsEnum.ViewJurisdictionStrategy ||
                            x.id == UserRightsEnum.ViewProjects ||
                            x.id == UserRightsEnum.ViewLocalCounselStrategy ||
                            x.id == UserRightsEnum.ViewDeposition ||
                            x.id == UserRightsEnum.ViewZoneNotes
                    ).length > 0
                )
                    return true;
                break;
            case LocalRoutes.ZoneNotes:
                return userRights.some((x) => x.id == UserRightsEnum.ViewZoneNotes);
            case LocalRoutes.PackageSettlements:
            case LocalRoutes.PackageSettlementDetails:
            case LocalRoutes.PackageSettlementDetailExport:
            case LocalRoutes.PackageSettlementDetailExportID:
                return userRights.some((x) => x.id == UserRightsEnum.ViewPackageSettlement);
            case LocalRoutes.PackageSettlementNew:
                return userRights.some(
                    (x) => x.id == UserRightsEnum.AddEditDeletePackageSettlement
                );
            case LocalRoutes.StrategicPlan:
                return userRights.some((x) => x.id == UserRightsEnum.ViewStrategicPlan);
            case LocalRoutes.StrategyExecutionPlan:
                return userRights.some((x) => x.id == UserRightsEnum.ViewExecutionPlan);
            case LocalRoutes.Experts:
            case LocalRoutes.ExpertDetail:
                return userRights.some((x) => x.id === UserRightsEnum.ViewExpert);
            case LocalRoutes.Snapshot:
                return userRights.some((x) => x.id == UserRightsEnum.Snapshot);
            case LocalRoutes.Trends:
                return userRights.some((x) => x.id == UserRightsEnum.KPIView);
            case LocalRoutes.Alerts:
                return userRights.some((x) => x.id == UserRightsEnum.Alerts);
            case LocalRoutes.WeeklyRecap:
            case LocalRoutes.MonthlyRecap:
                return userRights.some((x) => x.id == UserRightsEnum.Reports);
            case LocalRoutes.Triggers:
                return (
                    !isSystemAdmin &&
                    userRights.some((x) => x.id == UserRightsEnum.CaseTriggerRules)
                );
            case LocalRoutes.TriggerNew:
            case LocalRoutes.TriggerDetails:
                return !isSystemAdmin;
            case LocalRoutes.QuadCaseList:
                return !isCompanyUser && hasAtleastOneNationalDataScope;
            case LocalRoutes.QuadZoneNoteCreate:
                return true;
            case LocalRoutes.QuadSameCase:
            case LocalRoutes.QuadZoneNoteCreate:
            case LocalRoutes.QuadDashboard:
            case LocalRoutes.QuadSameCaseEasyUpdate:
            case LocalRoutes.QuadMessages:
                return userRights.some((x) => x.id === UserRightsEnum.AddZoneNote);
            case LocalRoutes.QuadAdminSameCaseManage:
            case LocalRoutes.QuadAdminSameCaseSearch:
                return Auth.isInApplicationRole(RolesEnum.SameCaseAdmin, user);
            default:
                return false;
        }

        return false;
    }

    static userHasAtLeastOneRight(
        user: IUserModel,
        rights: number[],
        currentZoneGuid?: string
    ): boolean {
        return rights.some((right) => this.userHasRight(right, user, currentZoneGuid));
    }

    static userHasAtLeastOneRightAcrossZones(
        user: IUserModel,
        rights: number[],
        zoneGuids: string[]
    ): boolean {
        return zoneGuids.some((zoneGuid) =>
            rights.some((right) => this.userHasRight(right, user, zoneGuid))
        );
    }

    static userHasRight(
        userRightValue: number,
        user: IUserModel,
        currentZoneGuid?: string
    ): boolean {
        if (!user.organizationRoles) return false;
        const foundOrg = user.organizationRoles.find((x) => x.guid == currentZoneGuid);
        return foundOrg?.rights?.some((x) => x.id === userRightValue) ?? false;
    }

    static userHasGlobalRight(userRightValue: number, user: IUserModel): boolean {
        if (!user.organizationRoles) return false;
        const userRights = user.organizationRoles?.flatMap((role) => role.rights);
        return userRights?.filter((x) => x?.id === userRightValue).length > 0;
    }

    static userHasAtleastOneTriggerRight(user: IUserModel): boolean {
        if (!user.organizationRoles) return false;

        return (
            user.organizationRoles
                .flatMap((x) => x.rights)
                .filter((x) => x?.id == UserRightsEnum.CaseTriggerRules).length > 0
        );
    }

    static getUserDefaultOrganizationDataScope(user: IUserModel, currentZoneGuid?: string): number {
        if (!user.organizationRoles) return 0;

        if (
            currentZoneGuid &&
            user.organizationRoles &&
            user.organizationRoles.filter((x) => x.guid == currentZoneGuid).length > 0
        ) {
            const orgMatch = user.organizationRoles.filter((x) => x.guid == currentZoneGuid)[0];
            if (orgMatch.dataScope) return orgMatch.dataScope.id;
        }
        return 0;
    }

    static isLocalScope(user: IUserModel, currentZoneGuid?: string): boolean {
        return (
            this.getUserDefaultOrganizationDataScope(user, currentZoneGuid) ===
            DataScopesEnum.LocalBasic.Value
        );
    }

    static showStrategyMenuItem(user?: IUserModel, currentZoneGuid?: string): boolean {
        const userRights = this.getUserActiveZoneRights(user, currentZoneGuid);
        return (
            userRights.filter(
                (x) =>
                    x.id == UserRightsEnum.ViewStrategicPlan ||
                    x.id == UserRightsEnum.ViewExecutionPlan ||
                    x.id == UserRightsEnum.ViewPlaintiffsFirmStrategy ||
                    x.id == UserRightsEnum.ViewJurisdictionStrategy ||
                    x.id == UserRightsEnum.ViewProjects ||
                    x.id == UserRightsEnum.ViewLocalCounselStrategy
            ).length > 0
        );
    }

    static showReviewMenuItem(user?: IUserModel, currentZoneGuid?: string): boolean {
        const userRights = this.getUserActiveZoneRights(user, currentZoneGuid);
        return (
            userRights.filter(
                (x) =>
                    x.id == UserRightsEnum.Board ||
                    x.id == UserRightsEnum.Snapshot ||
                    x.id == UserRightsEnum.Alerts ||
                    x.id == UserRightsEnum.KPIView
            ).length > 0
        );
    }

    static userHasViewStrategyNotesRight(user: IUserModel, currentZoneGuid?: string): boolean {
        const userRights = this.getUserActiveZoneRights(user, currentZoneGuid);
        return (
            userRights.filter(
                (x) =>
                    x.id == UserRightsEnum.ViewStrategicPlan ||
                    x.id == UserRightsEnum.ViewExecutionPlan ||
                    x.id == UserRightsEnum.ViewPlaintiffsFirmStrategy ||
                    x.id == UserRightsEnum.ViewJurisdictionStrategy ||
                    x.id == UserRightsEnum.ViewProjects ||
                    x.id == UserRightsEnum.ViewLocalCounselStrategy
            ).length > 0
        );
    }

    static allowOrganizationSwitch(user?: IUserModel): boolean {
        const isSystemAdmin = user ? Auth.isSystemAdmin(user) : false;
        const isOrganizationAdmin = user ? Auth.isOrganizationAdmin(user) : false;
        const isMasterAdmin = user ? Auth.isMasterAdmin(user) : false;

        return !isSystemAdmin && !isOrganizationAdmin && !isMasterAdmin;
    }

    static isAuthorizedToStrategyType(
        strategyType: string,
        user?: IUserModel,
        currentZoneGuid?: string
    ) {
        const userRights = this.getUserActiveZoneRights(user, currentZoneGuid);
        switch (strategyType) {
            case StrategyTypes.PlaintiffsFirm.Name:
                return (
                    userRights.filter((x) => x.id == UserRightsEnum.ViewPlaintiffsFirmStrategy)
                        .length > 0
                );
            case StrategyTypes.Jurisdiction.Name:
                return (
                    userRights.filter((x) => x.id == UserRightsEnum.ViewJurisdictionStrategy)
                        .length > 0
                );
            case StrategyTypes.LocalCounsel.Name:
                return (
                    userRights.filter((x) => x.id == UserRightsEnum.ViewLocalCounselStrategy)
                        .length > 0
                );
            case StrategyTypes.Projects.Name:
                return userRights.filter((x) => x.id == UserRightsEnum.ViewProjects).length > 0;
            case StrategyTypes.Experts.Name:
                return userRights.filter((x) => x.id == UserRightsEnum.ViewExpert).length > 0;
            default:
                break;
        }
    }

    static isReadOnly(user: IUserModel, zoneGuid: string): boolean {
        return (
            user.organizationRoles?.some(
                (x) => x.guid === zoneGuid && x.role?.id === RolesEnum.ReadOnlyUser
            ) ?? false
        );
    }

    static getUserOrganizationsWithRight(
        rightId: number,
        user?: IUserModel
    ): IOrganizationRoleModel[] {
        if (!user?.organizationRoles) return [];
        return [...user.organizationRoles].filter((org) =>
            org.rights?.some((right) => right.id === rightId)
        ) as IOrganizationRoleModel[];
    }

    static userHasAtleastOneNationalDataScope(user: IUserModel): boolean {
        return (
            user.organizationRoles?.some(
                (orgRole) => orgRole.dataScope?.id === DataScopesEnum.National.Value
            ) ?? false
        );
    }
}
